import { Col, DatePicker, Form, Row, Select } from "antd";
import Table from "atoms/Table";
import Title from "atoms/Title";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { getClientid, getUserInfo, getUserList } from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { utils, writeFileXLSX } from "xlsx";
import dayjs from "dayjs";
import styled from "styled-components";

function IndependenceAnalysis() {
  const userList = useSelector((state) => getUserList(state));
  const [selectedYear, setSelectedYear] = useState(moment().format("YYYY"));
  const [selectedManagingOffice, setSelectedManagingOffice] = useState("Kenya");
  const clientId = useSelector(getClientid);
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);

  const [form] = Form.useForm();

  const activeUsers = useMemo(() => {
    return userList?.filter((user) => user?.statusid !== "0");
  }, [userList]);

  const managingOfficeOptions = [
    { label: "Kenya", value: "Kenya" },
    { label: "Uganda", value: "Uganda" },
    { label: "Tanzania", value: "Tanzania" },
  ];

  const statusOptions = [
    { label: "Submitted", value: "submit" },
    { label: "Draft", value: "draft" },
    { label: "Pending", value: "pending" },
  ];

  const columns = [
    {
      title: "Name",
      dataIndex: "fullname",
      key: "fullname",
      className: "normal-column",
      render: (val) => <span className="text-primary">{val}</span>,
      onCell: (record) => ({
        onClick: () => {
          if (record?.IndependenceForm) {
            history.push(`/independence-analysis/${record.userid}`);
          }
        },
        style: { cursor: "pointer" },
      }),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      className: "normal-column",
      render: (val) => <span>{val ? val : "-"}</span>,
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      className: "normal-column",
      ellipsis: { showTitle: true },
      render: (val) => (
        <span>{val === "null" || val === "" ? "-" : val}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "userid",
      key: "person_email_address",
      className: "normal-column",
      render: (val) => (
        <span>{val === "null" || val === "" ? "-" : val}</span>
      ),
    },
    {
      title: "Submission Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        if (val === "draft") return "Draft";
        if (val === "pending") return "Pending";
        if (val === "submit") return "Submitted";
        return "-";
      },
    },
    {
      title: "Date Submitted",
      dataIndex: "time",
      key: "declarationSubmittedDate",
      className: "normal-column",
      render: (val) => (
        <span>
          {val && val !== undefined && val !== null && val !== "null"
            ? moment(val).format("DD-MM-YYYY")
            : "-"}
        </span>
      ),
    },
  ];

  const { getAllDeclaration } = queryKeys;
  const { key, url } = getAllDeclaration(clientId, selectedManagingOffice, selectedYear);
  const { data, isLoading } = useFetch(key, url);

  const dataList = useMemo(() => {
    if (!data || !activeUsers?.length) return [];
    const activeUserIds = new Set(activeUsers.map((u) => u.userid));
    const responseData = data?.response?.data || [];
    return responseData.filter(
      (item) =>
        activeUserIds.has(item.userid) &&
        item?.managingoffice === selectedManagingOffice
    );
  }, [data, activeUsers, selectedManagingOffice]);

  // ✅ Built from dataList so options always match what's in the table
  const userOptions = useMemo(() => {
    const seen = new Set();
    return dataList
      ?.filter((item) => {
        if (seen.has(item.userid)) return false;
        seen.add(item.userid);
        return true;
      })
      ?.map((item) => ({
        label: item?.fullname,
        value: item?.userid,
      }));
  }, [dataList]);

  const handleFormChange = () => {
    const values = form.getFieldsValue();

    // ✅ Reset name filter when country changes to avoid stale selections
    if (values.managingoffice && values.managingoffice !== selectedManagingOffice) {
      setSelectedManagingOffice(values.managingoffice);
      form.setFieldsValue({ name: [] });
    }

    if (values.year) {
      setSelectedYear(dayjs(values.year).format("YYYY"));
    }
  };

  const uniqueByUserYear = (arr) => {
    const map = new Map();
    arr.forEach((item) => {
      const k = `${item.userid}-${item.year}`;
      map.set(k, item);
    });
    return Array.from(map.values());
  };

  const getFilteredData = () => {
    const name = form.getFieldValue("name") || [];
    const status = form.getFieldValue("status") || [];
    const year = form.getFieldValue("year");

    const activeUserIds = new Set(
      userList
        ?.filter((user) => user.statusid === "1")
        ?.map((user) => user.userid)
    );

    let result = dataList?.filter((item) => activeUserIds.has(item.userid));

    // ✅ Name filter — matches userid from dataList
    if (name?.length > 0) {
      result = result?.filter((item) => name.includes(item?.userid));
    }

    if (status?.length > 0) {
      result = result?.filter((item) => status.includes(item?.status));
    }

    if (year && year?._d) {
      const yr = moment(year._d).format("YYYY");
      result = result?.filter((item) => String(item?.year) === yr);
    }

    return uniqueByUserYear(result);
  };

  const xport = React.useCallback(() => {
    const filteredData = getFilteredData();
    const exportData = filteredData.map((item) => ({
      Name: item.fullname || "-",
      Year: item.year || "-",
      Designation:
        item.designation === "null" || item.designation === ""
          ? "-"
          : item.designation || "-",
      Email:
        item.userid === "null" || item.userid === ""
          ? "-"
          : item.userid || "-",
      "Submission Status":
        item.status === "draft"
          ? "Draft"
          : item.status === "pending"
            ? "Pending"
            : item.status === "submit"
              ? "Submitted"
              : "-",
      "Date Submitted":
        item.time && item.time !== "null" && item.time !== null && item.time !== undefined
          ? moment(item.time).format("DD-MM-YYYY")
          : "-",
    }));

    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Annual Declaration");
    writeFileXLSX(wb, "Annual Declaration Report.csv");
  });

  return (
    <DeclarationReportWrap>
      <Row>
        <Col sm={24}>
          <div className="d-flex justify-content-between">
            <Title>Annual Declaration</Title>
            <ButtonOutlined
              className="text-end"
              disabled={!getFilteredData()?.length}
              onClick={xport}
            >
              Export CSV file
            </ButtonOutlined>
          </div>
        </Col>
        <Col sm={24}>
          <Form
            form={form}
            layout="vertical"
            className="mt-2"
            onValuesChange={handleFormChange}
            initialValues={{
              managingoffice: "Kenya",
              year: dayjs(),
            }}
          >
            <Row gutter={12} className="g-3 mb-2">
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <Form.Item name="name">
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder="Name"
                    options={userOptions}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <Form.Item name="managingoffice">
                  <Select
                    showSearch
                    placeholder="Select Country"
                    options={managingOfficeOptions}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <Form.Item name="status">
                  <Select
                    mode="multiple"
                    placeholder="Submission Status"
                    options={statusOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <Form.Item name="year" initialValue={dayjs()}>
                  <DatePicker
                    picker="year"
                    format="YYYY"
                    allowClear={false}
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "50px",
                      width: "100%",
                    }}
                    placeholder="Year"
                    disabledDate={(current) =>
                      current &&
                      (current.year() > new Date().getFullYear() ||
                        current.year() < 2025)
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={getFilteredData()}
        columns={columns}
        pagination={false}
        loading={isLoading}
        onRow={undefined}
        id="Table"
      />
    </DeclarationReportWrap>
  );
}

export default IndependenceAnalysis;

const DeclarationReportWrap = styled.div`
  .text-end {
    background-color: #fff;
    color: #000;
    border: 1px solid #000;
    :hover {
        border: 1px solid #000;
        background: #fff;
        color: #000;
    }
  }
`;